import { fieldTypes } from "@ucl/library";
import { FC, useRef } from "react";
import { FieldComplexV2 } from "./FieldComplexV2";
import { COMPLEX_FIELD_KEYS } from "@/constants/fieldKeys";
import "./ComplexFields.scss";
import { FinancialApplicationDocuments } from "@/types/FinancialApplicationDocuments";
import { BoltDocument, BoltDocumentFileUploaderProps } from "@bolt/document";
import { get, set } from "lodash";
import { ApplicationSubmissionDetails } from "@/types/FinancialApplication";

export interface FinancialApplicationDocumentsComplexValue {
  financialApplicationId?: string;
  showHeader?: boolean;
  applicationDetails?: ApplicationSubmissionDetails;
}

export interface FinancialApplicationDocumentsComplexFieldProps
  extends fieldTypes.BaseFieldProps<FinancialApplicationDocuments> {
  formValue: any;
  fieldPath: string;
  onValueChange: (newValue: any, newFieldValue: any, fieldProps: any) => void;
}

export const FinancialApplicationDocumentsComplexField: FC<
  FinancialApplicationDocumentsComplexFieldProps
> = (props) => {
  const localComplexValueRef = useRef({ ...props.value });
  const financialApplicationDocumentsComplexValue =
    props.complexFieldValue as FinancialApplicationDocumentsComplexValue;
  const uploaderProps = {
    entityType: "FinancialApplication",
    entityId: financialApplicationDocumentsComplexValue?.financialApplicationId,
  } as BoltDocumentFileUploaderProps;

  const applicationDetails = financialApplicationDocumentsComplexValue?.applicationDetails;

  const handleSubmit = (path: keyof FinancialApplicationDocuments, valueDocument: any) => {
    const boltDocument = valueDocument as BoltDocument;
    const newComplexValue = { ...localComplexValueRef.current };
    const pathPreviousValue = get(newComplexValue, path);

    // Set new Comma Delimited String for the path
    let pathNewValue = pathPreviousValue
      ? `${pathPreviousValue},${boltDocument.id}`
      : boltDocument.id;

    // Remove duplicates
    pathNewValue = [...new Set(pathNewValue.split(","))].join(",");

    // Update the local complex value
    set(newComplexValue, path, pathNewValue);
    localComplexValueRef.current = { ...newComplexValue };

    // Update the form value
    const newValue = { ...props.formValue };
    set(newValue, props.fieldPath, newComplexValue);
    props.onValueChange({ ...newValue }, newComplexValue, props);
  };

  const handleRemoving = (path: keyof FinancialApplicationDocuments, _id: string) => {
    const newComplexValue = { ...localComplexValueRef.current };
    const pathPreviousValue = get(newComplexValue, path);

    if (!pathPreviousValue) {
      return; // Nothing to remove
    }

    // Remove the document ID from the comma-delimited string
    const pathArray = pathPreviousValue.split(",").filter((id) => id !== id);
    const pathNewValue = pathArray.join(",");

    // Update the local complex value
    set(newComplexValue, path, pathNewValue);
    localComplexValueRef.current = { ...newComplexValue };

    // Update the form value
    const newValue = { ...props.formValue };
    set(newValue, props.fieldPath, newComplexValue);
    props.onValueChange({ ...newValue }, newComplexValue, props);
  };

  return (
    <section className="complex-field-container financial-application-documents">
      {financialApplicationDocumentsComplexValue?.showHeader && <h4>{props.label}</h4>}
      <div className="complex-field-content">
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS
              .ARTICLES_OF_INCORPORATION_OR_CERTIFICATE_OF_FORMATION_OR_ORGANIZATION
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit(
                  "articlesOfIncorporationOrCertificateOfFormationOrOrganization",
                  value
                );
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("articlesOfIncorporationOrCertificateOfFormationOrOrganization", id);
              },
              hidden:
                !applicationDetails?.hasBusinessAccount || !applicationDetails?.hasIoltaAccount,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.BYLAWS}
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("bylaws", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("bylaws", id);
              },
              hidden: !applicationDetails?.requireBylaws,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.OPERATING_AGREEMENT}
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("operatingAgreement", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("operatingAgreement", id);
              },
              hidden: !applicationDetails?.requireOperatingAgreement,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.PARTNERSHIP_AGREEMENT}
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("partnershipAgreement", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("partnershipAgreement", id);
              },
              hidden: !applicationDetails?.requirePartnershipAgreement,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.CORPORATE_RESOLUTION}
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("corporateResolution", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("corporateResolution", id);
              },
              hidden: !applicationDetails?.requireCorporateResolution,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.MEMBER_OR_PARTNER_RESOLUTION
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("memberOrPartnerResolution", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("memberOrPartnerResolution", id);
              },
              hidden: !applicationDetails?.requireMemberOrPartnerResolution,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.SIGNATURE_CARD}
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("signatureCard", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("signatureCard", id);
              },
              hidden: !applicationDetails?.hasDepositAccount,
            } as BoltDocumentFileUploaderProps
          }
        />
        {/* <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS
              .BUSINESS_LICENSE_OR_STATE_REGISTRATION_CERTIFICATE
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("businessLicenseOrStateRegistrationCertificate", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("businessLicenseOrStateRegistrationCertificate", id);
              },
              hidden: !applicationDetails?.requireBusinessLicense,
            } as BoltDocumentFileUploaderProps
          }
        /> */}
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.DBA_OR_ASSUMED_NAME_CERTIFICATE
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("dbaOrAssumedNameCertificate", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("dbaOrAssumedNameCertificate", id);
              },
              hidden: !applicationDetails?.hasTradeOrAssumedName,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.TREASURY_MANAGEMENT_AGREEMENT
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("treasuryManagementAgreement", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("treasuryManagementAgreement", id);
              },
              hidden: !applicationDetails?.hasTmServicesEnabled,
            } as BoltDocumentFileUploaderProps
          }
        />
        {/* <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.CDARS_DOCUMENTS}
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("cdarsDocuments", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("cdarsDocuments", id);
              },
              hidden: !applicationDetails?.hasCdarsEnabled,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.ICS_DOCUMENTS}
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("icsDocuments", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("icsDocuments", id);
              },
              hidden: !applicationDetails?.hasIcsEnabled,
            } as BoltDocumentFileUploaderProps
          }
        /> */}
        {/* <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS
              .ARIZONA_IOLTA_NOTIFICATION_TO_APPROVED_FI
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("arizonaIoltaNotificationToApprovedFi", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("arizonaIoltaNotificationToApprovedFi", id);
              },
              hidden: !applicationDetails?.isAccountTypeIsIoltaAz,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.COLORADO_COLTAF_ENROLLMENT_FORM
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("coloradoColtafEnrollmentForm", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("coloradoColtafEnrollmentForm", id);
              },
              hidden: !applicationDetails?.isAccountTypeIsIoltaCo,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.ILLINOIS_IOLTA_NOTICE_TO_FI
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("illinoisIoltaNoticeToFi", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("illinoisIoltaNoticeToFi", id);
              },
              hidden: !applicationDetails?.isAccountTypeIsIoltaIl,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.LOUISIANA_IOLTA_NOTICE_OF_ENROLLMENT
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("louisianaIoltaNoticeOfEnrollment", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("louisianaIoltaNoticeOfEnrollment", id);
              },
              hidden: !applicationDetails?.isAccountTypeIsIoltaLa,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.MISSOURI_IOLTA_NOTICE_TO_FI_AND_W9
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("missouriIoltaNoticeToFiAndW9", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("missouriIoltaNoticeToFiAndW9", id);
              },
              hidden: !applicationDetails?.isAccountTypeIsIoltaMo,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.OHIO_IOLTA_IOTA_ACCOUNT_OPENING_EMAIL
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("ohioIoltaIotaAccountOpeningEmail", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("ohioIoltaIotaAccountOpeningEmail", id);
              },
              hidden: !applicationDetails?.isAccountTypeIsIoltaOh,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.TENNESSEE_IOLTA_NOTICE_TO_FI
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("tennesseeIoltaNoticeToFi", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("tennesseeIoltaNoticeToFi", id);
              },
              hidden: !applicationDetails?.isAccountTypeIsIoltaTn,
            } as BoltDocumentFileUploaderProps
          }
        /> */}
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS
              .INCOME_STATEMENTS_CURRENT_YEAR_TO_DATE
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("incomeStatementsCurrentYearToDate", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("incomeStatementsCurrentYearToDate", id);
              },
              hidden: !applicationDetails?.hasCreditAccount,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.BALANCE_SHEET_LAST_2_YEARS
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("balanceSheetLast2Years", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("balanceSheetLast2Years", id);
              },
              hidden: !applicationDetails?.hasCreditAccount,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.PERSONAL_TAX_RETURNS_LAST_2_YEARS
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("personalTaxReturnsLast2Years", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("personalTaxReturnsLast2Years", id);
              },
              hidden: !applicationDetails?.hasCreditAccount,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.BUSINESS_TAX_RETURNS_LAST_2_YEARS
          }
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("businessTaxReturnsLast2Years", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("businessTaxReturnsLast2Years", id);
              },
              hidden: !applicationDetails?.hasCreditAccount,
            } as BoltDocumentFileUploaderProps
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.FINANCIAL_APPLICATION_DOCUMENTS.OTHER_DOCUMENTS}
          complexFieldProps={
            {
              ...uploaderProps,
              onSubmit: (value: any) => {
                handleSubmit("otherDocuments", value);
              },
              onRemoveDocument: (id: string) => {
                handleRemoving("otherDocuments", id);
              },
            } as BoltDocumentFileUploaderProps
          }
        />
      </div>
    </section>
  );
};
